// Shared Print-Preview styling + HTML builder cho MỌI dialog Print (TLC ERP).
//
// Mục tiêu (chị Giang chốt 2026-07-22): preview hiển thị NHƯ 1 tờ A4 THẬT —
// tờ giấy trắng full khổ A4 (210×297mm, hoặc 297×210mm landscape) NỔI trên nền
// xám, có đổ bóng → nhìn rõ khổ giấy + lề, giống style trang goods-allocation.
//
// Trước đây mỗi dialog copy-paste 1 bản `PRINT_PREVIEW_CSS` gần giống nhau (24 bản)
// + 1 hàm buildPreviewHtml. File này gom về 1 chỗ:
//   · `buildPrintPreviewHtml(rawHtml, opts)` — inject <base> + <style> override.
//   · CSS sinh theo `orientation` (portrait/landscape) + `layout`
//     (absolute report `.access-report/.ctl` vs flow grid) + `extraCss` cho outlier.
//
// Screen = WYSIWYG với PDF: cùng khổ A4 + lề; chỉ khác nền xám + bóng (bị bỏ khi
// @media print để PDF/in ra sạch). Mobile: tờ giấy full bề ngang, bỏ nền/bóng.

export interface PrintPreviewOptions {
  /** id của <style> tag (mỗi dialog 1 id riêng cho dễ debug). */
  styleId?: string
  /** Hướng giấy — 'landscape' cho report rộng (vd VGM). Mặc định 'portrait'. */
  orientation?: 'portrait' | 'landscape'
  /**
   * 'absolute' (mặc định): report absolute-positioned (`.access-report` + `.ctl`)
   *   → sheet padding nhỏ, `.access-report` tự căn giữa theo bề rộng gốc.
   * 'flow': report dạng grid/flow (content chảy thẳng trong `.report-page`)
   *   → sheet có padding lề A4 để content không chạm mép.
   */
  layout?: 'absolute' | 'flow'
  /** CSS bổ sung cho từng report (vd Moving gap giữa trang, VGM zoom). */
  extraCss?: string
  /**
   * true: tờ giấy cao THEO NỘI DUNG (không ép full khổ A4) — dùng cho report NGẮN
   * hơn hẳn 1 trang (vd VGM landscape ~135mm) để tránh khoảng trắng thừa nửa dưới
   * bị nhìn nhầm thành "trang 2". Mặc định false = ép full khổ A4 (thấy trọn trang).
   */
  fitContent?: boolean
  /**
   * CSS selector của phần tử = 1 TRANG in (vd '.pss-page' của report in gộp nhiều
   * trang). Khi set: MỖI phần tử này thành 1 tờ A4 riêng (nền trắng + bóng, cách nhau
   * khoảng trống trên nền xám), `.report-page` thành container TRONG SUỐT → preview
   * tách rõ TỪNG TRANG đúng như PDF. Report 1 trang KHÔNG cần (mặc định tờ giấy =
   * `.report-page`). Bỏ qua khi `fitContent`.
   */
  pageSelector?: string
  /** Padding mỗi tờ khi dùng pageSelector (mặc định theo layout). */
  pagePadding?: string
}

const SHEET = {
  portrait: { w: '210mm', h: '297mm', page: 'A4' },
  landscape: { w: '297mm', h: '210mm', page: 'A4 landscape' },
} as const

// Nền xám (slate-100) + bóng nhẹ — khớp look goods-allocation.
const BACKDROP = '#eef2f6'
const SHEET_SHADOW = '0 2px 14px rgba(15, 23, 42, 0.20)'

/** Sinh CSS override cho preview iframe. */
export function printPreviewCss(opts: PrintPreviewOptions = {}): string {
  const { orientation = 'portrait', layout = 'absolute', extraCss = '', fitContent = false, pageSelector, pagePadding } = opts
  const s = SHEET[orientation]
  // Padding tờ giấy:
  //  · fitContent → 0: report absolute đã gồm lề trong toạ độ; thêm padding dọc sẽ
  //    ĐẨY report cao gần bằng trang (vd VGM 193mm + 18mm padding > 210mm) tràn sang
  //    trang 2 khi export PDF. Bỏ padding → khớp Access, PDF đúng 1 trang = preview.
  //  · flow (grid) → cần lề A4 để content không sát mép.
  //  · absolute full-A4 → lề nhỏ (bề rộng report đã gồm lề Access, căn giữa ra lề ngang).
  const pad = pagePadding ?? (fitContent ? '0' : layout === 'flow' ? '9mm 11mm' : '9mm 10mm')
  // fitContent: cao theo nội dung (report ngắn); else: ép full khổ A4.
  const screenMinH = fitContent ? 'auto' : s.h
  // pageSelector (report nhiều trang): mỗi trang cao theo content + break-before tách
  // trang → KHÔNG ép full-height (tránh trang trắng thừa do khớp mép chính xác).
  const printMinH = fitContent || pageSelector ? '0' : s.h
  // "Tờ giấy" = phần tử nào: mặc định `.report-page`; nếu report nhiều trang thì mỗi
  // `pageSelector` là 1 tờ, `.report-page` chỉ là container trong suốt.
  const sheet = pageSelector ?? '.report-page'
  const sheetGap = pageSelector ? '16px' : '0'
  const containerReset = pageSelector
    ? `.report-page { background: transparent !important; box-shadow: none !important; padding: 0 !important; width: auto !important; max-width: none !important; min-height: auto !important; margin: 0 !important; }`
    : ''
  return `
@page { size: ${s.page} !important; margin: 0 !important; }

/* ── SCREEN: tờ A4 trắng nổi trên nền xám (WYSIWYG với bản in) ── */
body { margin: 0 !important; background: ${BACKDROP} !important; padding: 16px 0 !important; }
.print-action { display: none !important; }
${containerReset}
${sheet} {
  width: ${s.w} !important;
  max-width: none !important;
  min-height: ${screenMinH} !important;
  padding: ${pad} !important;
  margin: 0 auto ${sheetGap} !important;
  background: #fff !important;
  box-shadow: ${SHEET_SHADOW} !important;
  box-sizing: border-box !important;
}
.access-report { position: relative !important; margin: 0 auto !important; background: #fff !important; }
.ctl { position: absolute !important; overflow: visible !important; line-height: 1.2 !important; }
.ctl.bold { font-weight: 700 !important; }
.ctl.align-center { text-align: center !important; }
.ctl.align-right { text-align: right !important; }
.ctl.italic { font-style: italic !important; }
.ctl.cell { white-space: pre-wrap !important; word-break: normal !important; overflow-wrap: break-word !important; }
.ctl img { width: 100% !important; height: 100% !important; object-fit: contain !important; object-position: left top !important; display: block !important; }
.ctl.logo { overflow: hidden !important; }

/* ── PRINT / PDF: cùng khổ A4, bỏ nền xám + bóng ── */
@media print {
  @page { size: ${s.page} !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; padding: 0 !important; }
  ${pageSelector ? '.report-page { background: transparent !important; }' : ''}
  ${sheet} {
    width: ${s.w} !important; min-height: ${printMinH} !important; max-width: none !important;
    padding: ${pad} !important; margin: 0 !important; box-shadow: none !important; box-sizing: border-box !important;
  }
}

/* ── MOBILE: tờ giấy full bề ngang, bỏ nền/bóng ── */
@media screen and (max-width: 600px) {
  body { margin: 0 !important; background: #fff !important; padding: 0 !important; }
  ${sheet} {
    width: 100% !important; max-width: 100% !important; min-height: auto !important;
    padding: 4mm !important; margin: 0 auto ${sheetGap} !important; box-shadow: none !important;
  }
}
${extraCss ? `\n/* per-report */\n${extraCss}` : ''}
`.trim()
}

/** Inject <base href> (cho logo/asset) + <style> override vào HTML report từ BE. */
export function buildPrintPreviewHtml(rawHtml: string, opts: PrintPreviewOptions = {}): string {
  if (!rawHtml) return ''
  const styleId = opts.styleId ?? 'tlc-print-preview-overrides'
  const css = printPreviewCss(opts)
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="${styleId}">${css}</style>`
  return withBase.includes('</head>')
    ? withBase.replace('</head>', `${styleTag}</head>`)
    : `${styleTag}${withBase}`
}

/** Ký tự an toàn cho tên file PDF (bỏ ký tự cấm, space → '_'). */
export function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}
